import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Banner, downloadCsv, useQuery } from '@acc/web-shared';
import { PRESET_LABELS, REPORT_PRESETS, todayIn, type ReportPreset } from '@acc/domain';
import { PageHead, shortDate, useAgency } from '../agency';

interface CallExport {
  range: { from: string; to: string };
  calls: Array<{ callId: string; customerName: string; phone: string; reason: string; messageByMembershipId: string; ownerMembershipId: string | null; status: string; policyNumber: string | null; actionTaken: string | null; detailsNeeded: string | null; dueDate: string | null; callDate: string }>;
}
interface Row { membershipId: string | null; count: number }
interface Activity { range: { from: string; to: string }; callsLogged: Row[] | null; outgoingCalls: Row[] | null; currentQuoting: Row[]; assignedDuringPeriod: Row[] }
interface Missed {
  range: { from: string; to: string };
  pcMissed: number; lifeMissed: number; reopened: number; recovered: number; stillMissed: number;
  byStaff: Array<{ membershipId: string | null; pc: number; life: number; total: number }>;
  details: Array<{ opportunityId: string; customerName: string; coverage: 'PC' | 'Life'; ownerMembershipId: string | null; occurredAt: string; outcome: string }>;
}
interface Sales {
  range: { from: string; to: string };
  pcCompleted: number; lifeCompleted: number; total: number; customers: number; averageDaysToComplete: number | null;
  byStaff: Array<{ membershipId: string | null; pc: number; life: number; total: number; averageDays: number | null }>;
  byMonth: Array<{ month: string; pc: number; life: number; total: number }>;
  salesCycle: {
    total: number; completed: number; missed: number; averageDays: number | null; medianDays: number | null;
    bands: { within7: number; days8to30: number; over30: number };
    byStaff: Array<{ membershipId: string; count: number; averageDays: number | null }>;
    details: Array<{ opportunityId: string; customerName: string; coverage: 'PC' | 'Life'; outcome: 'Completed' | 'Missed'; membershipId: string; assignedAt: string; occurredAt: string; days: number }>;
  };

  details: Array<{ opportunityId: string; customerName: string; coverage: 'PC' | 'Life'; ownerMembershipId: string | null; occurredAt: string }>;
}

const TABS = [['activity', 'Staff activity'], ['missed', 'Missed & recovery'], ['sales', 'Sales performance']] as const;
type Tab = (typeof TABS)[number][0];

function Metric({ label, value }: { label: string; value: number | string | null }) {
  return <div className="stat"><div className="num">{value ?? '—'}</div><div className="lbl">{label}</div></div>;
}

export function Reports() {
  const a = useAgency();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some(([t]) => t === params.get('tab')) ? params.get('tab') : 'activity') as Tab;
  const preset = params.get('preset') ?? (params.get('from') ? '' : 'month');
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const qs = preset ? `preset=${preset}` : `from=${from}&to=${to}`;
  const restricted = tab !== 'activity' && !a.manager;
  const update = (patch: Record<string, string>) => {
    const next = { tab, preset, from, to, ...patch };
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)));
  };

  return (
    <>
      <div className="reports-print-sheet">
      <div className="reports-print-header"><h1>Agency Command Center — Reports</h1><p>{TABS.find(([t]) => t === tab)?.[1]} · Agency-local dates (Central time)</p></div>
      <PageHead title="Reports" sub="Totals use agency-local dates (Central time)." />
      <div className="reports-screen-controls"><div className="tabs" role="tablist">
        {TABS.filter(([t]) => t === 'activity' || a.manager).map(([t, l]) => <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => update({ tab: t })}>{t === 'activity' && !a.manager ? 'Quoting report' : l}</button>)}
      </div>
      <div className="toolbar">
        <select className="inline" aria-label="Date range" value={preset || 'custom'} onChange={(e) => (e.target.value === 'custom' ? update({ preset: '', from: from || todayIn(), to: to || todayIn() }) : update({ preset: e.target.value, from: '', to: '' }))}>
          {REPORT_PRESETS.map((p) => <option key={p} value={p}>{PRESET_LABELS[p as ReportPreset]}</option>)}
          <option value="custom">Custom dates…</option>
        </select>
        {!preset && (
          <>
            <label className="sr-only" htmlFor="rep-from">From</label><input id="rep-from" className="inline" type="date" value={from} onChange={(e) => update({ from: e.target.value })} style={{ height: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px' }} />
            <label className="sr-only" htmlFor="rep-to">To</label><input id="rep-to" className="inline" type="date" value={to} onChange={(e) => update({ to: e.target.value })} style={{ height: 36, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px' }} />
          </>
        )}
      </div>
      <div className="toolbar"><button type="button" className="btn" onClick={() => window.print()}>Print / Save as PDF</button></div>
      </div>

      <style>{`
        .reports-print-header { display: none; }
        @media print {
          @page { size: portrait; margin: 12mm; }
          html, body, #root { width: 100% !important; min-width: 0 !important; height: auto !important; overflow: visible !important; background: white !important; color: #111827 !important; }
          body * { visibility: hidden !important; }
          .reports-print-sheet, .reports-print-sheet * { visibility: visible !important; }
          .reports-print-sheet { position: absolute !important; inset: 0 auto auto 0 !important; width: 100% !important; max-width: none !important; min-width: 0 !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
          .reports-print-header { display: block !important; margin: 0 0 14px; color: #111827; }
          .reports-print-header h1 { font-size: 18pt; margin: 0 0 4px; }
          .reports-print-header p { margin: 0; font-size: 10pt; }
          .reports-screen-controls, .reports-screen-controls *, .reports-print-sheet > .page-head { display: none !important; }
          .reports-print-sheet .cards { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 8px !important; }
          .reports-print-sheet .grid2 { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 12px !important; }
          .reports-print-sheet .card, .reports-print-sheet .stat { width: auto !important; max-width: none !important; min-width: 0 !important; box-shadow: none !important; border: 1px solid #d1d5db !important; break-inside: avoid; }
          .reports-print-sheet .stat { padding: 8px !important; }
          .reports-print-sheet .card { padding: 12px !important; }
          .reports-print-sheet table { width: 100% !important; table-layout: auto !important; border-collapse: collapse !important; font-size: 9pt; }
          .reports-print-sheet th, .reports-print-sheet td { overflow-wrap: anywhere; padding: 5px 6px !important; }
          .reports-print-sheet tr { break-inside: avoid; }
          .reports-print-sheet thead { display: table-header-group; }
          .reports-print-sheet .toolbar { display: none !important; }
          .reports-print-sheet section:has(> h2) { margin-bottom: 12px; }
        }
      `}</style>
      {restricted ? <Banner tone="warn">Missed and sales reports are for the agency owner and office manager.</Banner>
        : tab === 'activity' ? <ActivityTab qs={qs} /> : tab === 'missed' ? <MissedTab qs={qs} /> : <SalesTab qs={qs} />}
      </div>
    </>
  );
}

function RangeNote({ range }: { range: { from: string; to: string } }) {
  return <p className="sub" style={{ marginBottom: 12 }}>{shortDate(range.from)} – {shortDate(range.to)}</p>;
}

function CountTable({ title, rows, note }: { title: string; rows: Row[]; note?: string }) {
  const a = useAgency();
  return (
    <section className="card">
      <h2>{title}</h2>
      {note && <p className="sub" style={{ marginBottom: 8 }}>{note}</p>}
      {rows.length === 0 ? <div className="empty">None in this period.</div> : (
        <table className="data"><thead><tr><th>Staff member</th><th style={{ textAlign: 'right' }}>Number</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.membershipId ?? 'none'}><td>{a.name(r.membershipId)}</td><td style={{ textAlign: 'right' }}><strong>{r.count}</strong></td></tr>)}</tbody>
        </table>
      )}
    </section>
  );
}

function ActivityTab({ qs }: { qs: string }) {
  const a = useAgency();
  const q = useQuery<Activity>(a.path(`/reports/activity?${qs}`));
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  const d = q.data;
  return (
    <>
      <RangeNote range={d.range} />
      {a.manager && <CallExports qs={qs} />}
      <div className="grid2">
        {d.callsLogged && <CountTable title="Calls logged" rows={d.callsLogged} note="By the person who took the message, by call date." />}
        {d.outgoingCalls && <CountTable title="Outgoing calls" rows={d.outgoingCalls} />}
        <CountTable title="Current P/C quoting workload" rows={d.currentQuoting} note="Live P/C Quoting assignments, regardless of date." />
        <CountTable title="Quotes assigned during period" rows={d.assignedDuringPeriod} note="Unique quotes assigned at any time in the range; reassigned quotes count for both people." />
      </div>
    </>
  );
}

function CallExports({ qs }: { qs: string }) {
  const a = useAgency();
  const [staff, setStaff] = useSearchParams();
  const selected = staff.get('callStaff') ?? '';
  const query = a.path(`/reports/calls/export?${qs}${selected ? `&membershipId=${encodeURIComponent(selected)}` : ''}`);
  const q = useQuery<CallExport>(query);
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading call export…</p>;
  const calls = q.data.calls;
  const exportCalls = (outgoingOnly: boolean) => {
    const rows = (outgoingOnly ? calls.filter((c) => c.status === 'Outgoing') : calls).map((c) => [
      c.callDate, c.customerName, c.phone, c.reason, a.name(c.messageByMembershipId),
      a.name(c.ownerMembershipId), c.status, c.policyNumber ?? '', c.actionTaken ?? '',
      c.detailsNeeded ?? '', c.dueDate ?? '',
    ]);
    downloadCsv(`calls-${outgoingOnly ? 'outgoing-' : ''}${q.data!.range.from}-to-${q.data!.range.to}.csv`,
      ['Call date', 'Customer', 'Phone', 'Reason', 'Message by', 'Assigned to', 'Status', 'Policy number', 'Action taken', 'Details needed', 'Due date'], rows);
  };
  return <section className="card">
    <h2>Phone call exports</h2>
    <p className="sub">Owner/Manager only. Exports respect the selected report dates and staff filter.</p>
    <div className="toolbar">
      <label>Staff member <select className="inline" aria-label="Call export staff" value={selected} onChange={(e) => {
        const n = new URLSearchParams(staff); if (e.target.value) n.set('callStaff', e.target.value); else n.delete('callStaff'); setStaff(n);
      }}><option value="">All staff</option>{a.team.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}</select></label>
      <button type="button" className="btn" onClick={() => exportCalls(false)}>Export all calls CSV ({calls.length})</button>
      <button type="button" className="btn" onClick={() => exportCalls(true)}>Export outgoing calls CSV ({calls.filter((c) => c.status === 'Outgoing').length})</button>
    </div>
  </section>;
}

function MissedTab({ qs }: { qs: string }) {
  const a = useAgency();
  const [params, setParams] = useSearchParams();
  const coverage = params.get('coverage') ?? '';
  const staff = params.get('staff') ?? '';
  const q = useQuery<Missed>(a.path(`/reports/missed?${qs}${coverage ? `&coverage=${coverage}` : ''}${staff ? `&membershipId=${staff}` : ''}`));
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n); };
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  const d = q.data;
  return (
    <>
      <div className="toolbar">
        <select className="inline" aria-label="Coverage" value={coverage} onChange={(e) => set('coverage', e.target.value)}><option value="">P/C and Life</option><option value="PC">P/C</option><option value="Life">Life</option></select>
        <select className="inline" aria-label="Employee" value={staff} onChange={(e) => set('staff', e.target.value)}><option value="">All employees</option>{a.team.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}</select>
        <button className="btn" type="button" onClick={() => downloadCsv(`missed-${d.range.from}-to-${d.range.to}.csv`, ['Customer', 'Coverage', 'Employee at miss', 'Missed date', 'Outcome'], d.details.map((e) => [e.customerName, e.coverage === 'PC' ? 'P/C' : 'Life', a.name(e.ownerMembershipId), e.occurredAt.slice(0, 10), e.outcome]))}>Export CSV</button>
      </div>
      <RangeNote range={d.range} />
      <div className="cards">
        <Metric label="P/C missed" value={d.pcMissed} /><Metric label="Life missed" value={d.lifeMissed} />
        <Metric label="Reopened" value={d.reopened} /><Metric label="Recovered to completed" value={d.recovered} /><Metric label="Still missed" value={d.stillMissed} />
      </div>
      <div className="grid2">
        <section className="card"><h2>By employee at the time of the miss</h2>
          {d.byStaff.length === 0 ? <div className="empty">No misses in this period.</div> : (
            <table className="data"><thead><tr><th>Employee</th><th>P/C</th><th>Life</th><th>Total</th></tr></thead>
              <tbody>{d.byStaff.map((r) => <tr key={r.membershipId ?? 'none'}><td>{a.name(r.membershipId)}</td><td>{r.pc}</td><td>{r.life}</td><td><strong>{r.total}</strong></td></tr>)}</tbody></table>
          )}
        </section>
        <section className="card"><h2>Details</h2>
          {d.details.length === 0 ? <div className="empty">No misses in this period.</div> : (
            <table className="data"><thead><tr><th>Customer</th><th>Coverage</th><th>Missed</th><th>Outcome</th></tr></thead>
              <tbody>{d.details.map((e, i) => <tr key={i}><td><Link to={`${a.base}/quotes/${e.opportunityId}`}>{e.customerName}</Link></td><td>{e.coverage === 'PC' ? 'P/C' : 'Life'}</td><td className="muted">{shortDate(e.occurredAt)}</td><td><span className={`pill ${e.outcome === 'Recovered' ? 'done' : e.outcome === 'Reopened' ? 'ip' : 'missed'}`}>{e.outcome}</span></td></tr>)}</tbody></table>
          )}
        </section>
      </div>
    </>
  );
}

function SalesTab({ qs }: { qs: string }) {
  const a = useAgency();
  const q = useQuery<Sales>(a.path(`/reports/sales?${qs}`));
  const maxMonth = useMemo(() => Math.max(1, ...(q.data?.byMonth ?? []).map((m) => m.total)), [q.data]);
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  const d = q.data;
  return (
    <>
      <div className="toolbar">
        <button className="btn" type="button" onClick={() => downloadCsv(`sales-detail-${d.range.from}-to-${d.range.to}.csv`, ['Customer', 'Coverage', 'Credited to', 'Completed date', 'Opportunity ID'], d.details.map((e) => [e.customerName, e.coverage === 'PC' ? 'P/C' : 'Life', a.name(e.ownerMembershipId), e.occurredAt.slice(0, 10), e.opportunityId]))}>Export sales details CSV</button>
        <button className="btn" type="button" onClick={() => downloadCsv(`sales-summary-${d.range.from}-to-${d.range.to}.csv`, ['Section', 'Staff member', 'P/C completed', 'Life completed', 'Total sales', 'Customers', 'Average days to complete', 'From', 'To'], [
          ['Agency total', 'All staff', d.pcCompleted, d.lifeCompleted, d.total, d.customers, d.averageDaysToComplete ?? '', d.range.from, d.range.to],
          ...d.byStaff.map((e) => ['Staff total', a.name(e.membershipId), e.pc, e.life, e.total, '', e.averageDays ?? '', d.range.from, d.range.to])
        ])}>Export sales summary CSV</button>
      </div>
      <RangeNote range={d.range} />
      <div className="cards">
        <Metric label="P/C completed" value={d.pcCompleted} /><Metric label="Life completed" value={d.lifeCompleted} />
        <Metric label="Coverage sales" value={d.total} /><Metric label="Customers" value={d.customers} />
        <Metric label="Avg. days to complete" value={d.averageDaysToComplete} />
      </div>
      <section className="card">
        <h2>Sales cycle — assignment to outcome</h2>
        <p className="sub">Days from the responsible staff member's assignment to a completed or missed coverage outcome. Each P/C or Life outcome is counted separately.</p>
        <div className="cards">
          <Metric label="Completed outcomes" value={d.salesCycle.completed} />
          <Metric label="Missed outcomes" value={d.salesCycle.missed} />
          <Metric label="Average days" value={d.salesCycle.averageDays} />
          <Metric label="Median days" value={d.salesCycle.medianDays} />
          <Metric label="0–7 days" value={d.salesCycle.bands.within7} />
          <Metric label="8–30 days" value={d.salesCycle.bands.days8to30} />
          <Metric label="Over 30 days" value={d.salesCycle.bands.over30} />
        </div>
        <h3>Sales cycle by staff member</h3>
        {d.salesCycle.byStaff.length === 0 ? <div className="empty">No assigned completed or missed outcomes in this period.</div> : (
          <table className="data"><thead><tr><th>Staff member</th><th>Outcomes</th><th>Avg. days</th></tr></thead>
            <tbody>{d.salesCycle.byStaff.map(row => <tr key={row.membershipId}><td>{a.name(row.membershipId)}</td><td>{row.count}</td><td>{row.averageDays ?? '—'}</td></tr>)}</tbody></table>
        )}
        <div className="toolbar">
          <button className="btn" type="button" onClick={() => downloadCsv(`sales-cycle-${d.range.from}-to-${d.range.to}.csv`, ['Customer', 'Coverage', 'Outcome', 'Staff member', 'Assigned', 'Outcome date', 'Days'], d.salesCycle.details.map(e => [e.customerName, e.coverage, e.outcome, a.name(e.membershipId), e.assignedAt.slice(0, 10), e.occurredAt.slice(0, 10), e.days]))}>Export sales cycle CSV</button>
        </div>
        <h3>Outcome details</h3>
        {d.salesCycle.details.length === 0 ? <div className="empty">No outcomes to show.</div> : (
          <table className="data"><thead><tr><th>Customer</th><th>Coverage</th><th>Outcome</th><th>Staff</th><th>Days</th></tr></thead>
            <tbody>{d.salesCycle.details.map((e, i) => <tr key={`${e.opportunityId}-${e.coverage}-${e.occurredAt}-${i}`}><td>{e.customerName}</td><td>{e.coverage === 'PC' ? 'P/C' : 'Life'}</td><td>{e.outcome}</td><td>{a.name(e.membershipId)}</td><td>{e.days}</td></tr>)}</tbody></table>
        )}
      </section>
      <div className="grid2">
        <section className="card"><h2>By staff member</h2>
          {d.byStaff.length === 0 ? <div className="empty">No completed coverage in this period.</div> : (
            <table className="data"><thead><tr><th>Staff</th><th>P/C</th><th>Life</th><th>Total</th><th>Avg. days</th></tr></thead>
              <tbody>{d.byStaff.map((r) => <tr key={r.membershipId ?? 'none'}><td>{a.name(r.membershipId)}</td><td>{r.pc}</td><td>{r.life}</td><td><strong>{r.total}</strong></td><td className="muted">{r.averageDays ?? '—'}</td></tr>)}</tbody></table>
          )}
        </section>
        <section className="card"><h2>By month</h2>
          {d.byMonth.length === 0 ? <div className="empty">No completed coverage in this period.</div> : d.byMonth.map((m) => (
            <div key={m.month} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 32px', gap: 10, alignItems: 'center', margin: '8px 0', fontSize: 13 }}>
              <span className="muted">{m.month}</span>
              <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', background: '#eef2f7' }} role="img" aria-label={`${m.month}: ${m.pc} P/C, ${m.life} Life`}>
                <div style={{ width: `${(m.pc / maxMonth) * 100}%`, background: '#2563eb' }} /><div style={{ width: `${(m.life / maxMonth) * 100}%`, background: '#7c3aed' }} />
              </div>
              <strong style={{ textAlign: 'right' }}>{m.total}</strong>
            </div>
          ))}
          {d.byMonth.length > 0 && <p className="sub" style={{ fontSize: 12 }}><span style={{ color: '#2563eb' }}>■</span> P/C &nbsp; <span style={{ color: '#7c3aed' }}>■</span> Life</p>}
        </section>
      </div>
    </>
  );
}
